import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal } from '@evertrue/et-components';
import EverTrue from 'app';
import CadenceDetailBreadcrumb from './cadence-detail-breadcrumb';
import CadenceDetailHeader from './cadence-detail-header';
import OverallPerformanceTable from './overall-performance-table';
import StepFilterHeader from './step-filter-header';
import StepStatsSummary from './step-stats-summary';
import CadencePerformanceChart from './cadence-performance-chart';
import CadenceRecipientsTable from './cadence-recipients-table';
import RecipientDetailPanel from './recipient-detail-panel';
import ContactPicker from 'apps/contact/components/contact-picker';
import DuplicateCadenceModal from '../duplicate/duplicate-cadence-modal';
import CadenceSharingModal from '../sharing/cadence-sharing-modal';
import ConfirmModal from '../shared/confirm-modal';
import CadenceAudienceSources from './cadence-audience-sources';
import useCadence from '../../hooks/use-cadence';
import useDomainStatus from '../../hooks/use-domain-status';
import useCadenceStats from '../../hooks/use-cadence-stats';
import useCadenceMetricProgress from '../../hooks/use-cadence-metric-progress';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import useDetailPageActions from '../../hooks/use-detail-page-actions';
import { useCadenceJobs, useJobPoller } from '../../hooks/use-job-poller';
import {
  mapCadenceToDetail,
  mapEventsToDetailSteps,
  METRIC_TYPE_LABELS,
  getRoundedPercentage,
} from '../../utils/cadence-data-mappers';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import { STEP_RECIPIENTS_QUERY, CADENCE_QUERY } from '../../utils/cadence-queries';
import { hasAutomatedEmailSteps } from '../../utils/cadence-step-utils';

/**
 * Multi-step Cadence detail page.
 * Sections: Breadcrumb, Header Card with Goal, Overall Performance Table,
 * Step Performance & Recipients with chart + table, Recipient Detail Panel.
 */
const CadenceDetailPage = ({ cadenceId }) => {
  const { cadence: rawCadence, loading: cadenceLoading, error: cadenceError, refetch } = useCadence(cadenceId);
  const [timeframeFilter, setTimeframeFilter] = useState('all_time');
  const {
    stepStats,
    goalProgress,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useCadenceStats(cadenceId, {
    stepStatsTimeRange:
      {
        '1_week': 'DAYS_7',
        '1_month': 'DAYS_30',
        '3_months': 'DAYS_90',
        '6_months': 'DAYS_180',
        '12_months': 'DAYS_365',
        all_time: 'ALL_TIME',
      }[timeframeFilter] || 'ALL_TIME',
  });
  const { metrics: metricProgressList, refetch: refetchMetricProgress } = useCadenceMetricProgress(cadenceId);

  // Check for in-progress enrollment jobs (bulk enroll, list import, or saved search import)
  const { jobs: enrollJobs, refetch: refetchJobs } = useCadenceJobs(cadenceId);
  const activeJob = enrollJobs.find((j) => !['COMPLETED', 'FAILED'].includes(j.status));
  const [pollingJobId, setPollingJobId] = useState(null);
  const { job: polledJob, isComplete: jobComplete, isFailed: jobFailed } = useJobPoller(pollingJobId);

  useEffect(() => {
    if (activeJob && !pollingJobId) {
      setPollingJobId(activeJob.id);
    }
  }, [activeJob, pollingJobId]);

  useEffect(() => {
    if (jobComplete || jobFailed) {
      setPollingJobId(null);
      refetchJobs();
      refetch();
      refetchStats();
    }
  }, [jobComplete, jobFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrollmentInProgress = pollingJobId && polledJob && !jobComplete && !jobFailed;

  // Derive permissions directly from canEdit/canDelete flags (same as template-detail-page.js
  // and list-cell-renderers.js). Backend availableActions is unreliable — it may return
  // non-empty arrays with values that don't match expected keys.
  const canView = !!rawCadence?.canView;
  const canEdit = !!rawCadence?.canEdit;
  const canDelete = !!rawCadence?.canDelete;
  // Share and recipient management are truly owner-only — canDelete can be granted via sharing.
  const currentUserId = EverTrue.store?.user?.get('id');
  const isCadenceOwner = currentUserId != null && String(rawCadence?.ownerUserId) === String(currentUserId);
  const cadenceStatusLower = (rawCadence?.displayStatusLabel || rawCadence?.status || '').toLowerCase();
  const availableActions = (() => {
    const computed = [];
    // Viewer and above: non-destructive read actions
    if (canView) {
      computed.push('duplicate');
    }
    // Editor and above: content editing
    if (canEdit) {
      computed.push('edit');
    }
    // canDelete: status transitions, delete, archive (can be granted via sharing)
    if (canDelete) {
      if (cadenceStatusLower === 'active') computed.push('pause');
      else if (cadenceStatusLower === 'paused') computed.push('resume');
      if (cadenceStatusLower === 'draft') computed.push('activate');
      computed.push('delete', 'archive');
    }
    // Cadence owner only: sharing and recipient management
    if (isCadenceOwner) {
      computed.push('share', 'remove_members');
      // Suppress manual enrollment when enrollment is locked
      if (!rawCadence?.enrollmentLocked) computed.push('enroll_members');
    }
    return computed;
  })();

  const cadence = useMemo(
    () => (rawCadence ? mapCadenceToDetail(rawCadence, goalProgress) : null),
    [rawCadence, goalProgress]
  );

  const steps = useMemo(
    () => (rawCadence ? mapEventsToDetailSteps(rawCadence.events, stepStats, rawCadence.edges) : []),
    [rawCadence, stepStats]
  );

  const actions = useDetailPageActions({
    entityId: cadenceId,
    entity: cadence,
    rawEntity: rawCadence,
    refetch,
    entityType: 'cadence',
  });
  const {
    removeCadenceMember,
    deleteCadence,
    recalculateCadenceStats,
    reevaluateSuccessMetrics,
    refreshCadenceSavedSearches,
    skipCadenceEvent,
    updateCadence,
    activateCadence,
    createTaskCleanupJob,
    deleteCadenceAudience,
  } = useCadenceMutations();
  const { status: domainStatus } = useDomainStatus();
  const isOwner = EverTrue.store?.user?.isSuperUser?.() || EverTrue.store?.user?.isOwner?.();
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const [selectedStepFilter, setSelectedStepFilter] = useState('full_cadence');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [stepMemberIds, setStepMemberIds] = useState(null);
  const [stepRecipientMap, setStepRecipientMap] = useState(null);
  const [stepRecipientsRefreshCount, setStepRecipientsRefreshCount] = useState(0);
  const stepPerformanceRef = useRef(null);
  const stepIdApplied = useRef(false);

  // Auto-select step and scroll to Step Performance when ?stepId= is in URL
  useEffect(() => {
    if (stepIdApplied.current || steps.length === 0) return;
    const stepId = EverTrue.UrlManager?.get('stepId');
    if (!stepId) return;
    const match = steps.find((s) => String(s.id) === String(stepId));
    if (match) {
      setSelectedStepFilter(String(match.id));
      stepIdApplied.current = true;
      // Scroll after a tick to allow render
      setTimeout(() => {
        stepPerformanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [steps]);
  const [blueprintName, setBlueprintName] = useState(null);

  // Look up blueprint name if cadence was created from one
  useEffect(() => {
    const templateId = cadence?.sourceCadenceTemplateId;
    if (!templateId) {
      setBlueprintName(null);
      return;
    }
    let cancelled = false;
    cadenceGqlCall(CADENCE_QUERY, { id: String(templateId) })
      .then((data) => {
        if (!cancelled && data?.cadence?.name) {
          setBlueprintName(data.cadence.name);
        }
      })
      .catch(() => {
        if (!cancelled) setBlueprintName(`Blueprint #${templateId}`);
      });
    return () => {
      cancelled = true;
    };
  }, [cadence?.sourceCadenceTemplateId]);

  useEffect(() => {
    if (!selectedStepFilter || selectedStepFilter === 'full_cadence') {
      setStepMemberIds(null);
      setStepRecipientMap(null);
      return;
    }
    let cancelled = false;
    cadenceGqlCall(STEP_RECIPIENTS_QUERY, {
      cadenceId: String(cadenceId),
      cadenceEventId: String(selectedStepFilter),
      pagination: { first: 500 },
    })
      .then((data) => {
        if (!cancelled && data?.stepRecipients) {
          const recipients = (data.stepRecipients.edges || []).map((e) => e.node);
          setStepMemberIds(new Set(recipients.map((r) => String(r.cadenceMemberId))));
          const rMap = {};
          recipients.forEach((r) => {
            rMap[String(r.cadenceMemberId)] = {
              selectedStep: r.selectedStep,
              currentStatus: r.currentStatus,
              deliveryStatus: r.deliveryStatus,
              emailDelivered: r.emailDelivered,
              emailOpened: r.emailOpened,
              emailClicked: r.emailClicked,
              emailReplied: r.emailReplied,
              emailBounced: r.emailBounced,
              emailUnsubscribed: r.emailUnsubscribed,
              taskCompleted: r.taskCompleted,
              taskSkipped: r.taskSkipped,
              isSuccessful: r.isSuccessful,
              exitedAtThisStep: r.exitedAtThisStep,
            };
          });
          setStepRecipientMap(rMap);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStepMemberIds(null);
          setStepRecipientMap(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cadenceId, selectedStepFilter, stepRecipientsRefreshCount]);

  const handleRemoveRecipient = async (recipient) => {
    const result = await removeCadenceMember({
      cadenceId: String(cadenceId),
      memberId: String(recipient.id),
      removalReason: 'manual',
    });
    if (result?.cadenceMember) {
      EverTrue.Alert.success(`${recipient.contactName || 'Contact'} removed from cadence`);
      setSelectedRecipient(null);
      refetch();
      refetchStats();
      setStepRecipientsRefreshCount((c) => c + 1);
    } else {
      EverTrue.Alert.error('Failed to remove contact from cadence');
    }
  };

  const handleSkipStep = async (cId, memberId) => {
    try {
      const result = await skipCadenceEvent(cId, memberId);
      if (result?.success) {
        EverTrue.Alert.success('Step skipped');
        setSelectedRecipient(null);
        refetch();
        refetchStats();
        setStepRecipientsRefreshCount((c) => c + 1);
      } else {
        EverTrue.Alert.error('Failed to skip step');
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to skip step: ${err.message}`);
    }
  };

  const handleRefreshMetrics = async () => {
    setRefreshingMetrics(true);
    try {
      // Fire-and-trust: kick off the saved-search refresh in parallel with the
      // metric recompute. The contacts service takes a few minutes to settle
      // tag membership after the refresh signal is received, so the metric
      // values rendered immediately may not yet reflect the refreshed tags.
      // Re-clicking Refresh Metrics in a few minutes picks up the change.
      // Wrap the saved-search call so its failure can't break the metric refresh.
      await Promise.all([
        recalculateCadenceStats(cadenceId),
        reevaluateSuccessMetrics(cadenceId),
        refreshCadenceSavedSearches(cadenceId).catch((err) => {
          // Non-fatal: metric values may be slightly stale this click but the
          // metric eval still ran. Log and continue.
          console.warn('Saved-search refresh signal failed:', err);
        }),
      ]);
      await refetch();
      await refetchStats();
      refetchMetricProgress();
      EverTrue.Alert.success('Metrics refreshed');
    } catch (err) {
      EverTrue.Alert.error(`Failed to refresh metrics: ${err.message}`);
    } finally {
      setRefreshingMetrics(false);
    }
  };

  const handleBackToCadences = () => {
    EverTrue.Navigator('/cadences-v2?tab=outreach', true);
  };

  const breadcrumbs = [
    { label: 'Cadences', onClick: handleBackToCadences },
    { label: 'Cadences & Plans', onClick: handleBackToCadences },
  ];

  if (cadenceLoading) {
    return (
      <div className="cadence-detail-page">
        <div className="cadence-detail-page--loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (cadenceError) {
    return (
      <div className="cadence-detail-page">
        <div className="cadence-detail-page--error">
          <Icon icon="error-outline" size={2} />
          <h3>Failed to load cadence</h3>
          <p>{cadenceError}</p>
          <Button type="secondary" onClick={handleBackToCadences}>
            Back to Cadences
          </Button>
        </div>
      </div>
    );
  }

  if (!cadence) {
    return (
      <div className="cadence-detail-page">
        <div className="cadence-detail-page--error">
          <Icon icon="error-outline" size={2} />
          <h3>Cadence not found</h3>
          <Button type="secondary" onClick={handleBackToCadences}>
            Back to Cadences
          </Button>
        </div>
      </div>
    );
  }

  const selectedStep =
    selectedStepFilter && selectedStepFilter !== 'full_cadence'
      ? steps.find((s) => String(s.id) === String(selectedStepFilter))
      : null;
  return (
    <div className="cadence-detail-page">
      <CadenceDetailBreadcrumb
        crumbs={breadcrumbs}
        currentName={cadence.name}
        className="cadence-detail-page--breadcrumb"
      />

      {rawCadence?.pausedReason === 'dns_disconnect' &&
        hasAutomatedEmailSteps(rawCadence?.events || []) && (
        <div className="cadence-detail-page--dns-pause-notice">
          <div className="cadence-detail-page--dns-pause-notice-title">
            <Icon icon="warning" />
            <strong>Cadence Auto-Paused — Sending Domain Disconnected</strong>
          </div>
          <p className="cadence-detail-page--dns-pause-notice-body">
            This cadence was automatically paused
            {rawCadence?.pausedAt ? (
              <>
                {' '}
                on{' '}
                <strong>
                  {new Date(rawCadence.pausedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </strong>
              </>
            ) : null}{' '}
            because your organization&apos;s sending domain was disconnected. Enrolled members remain in place and will
            continue from their current step once the domain is re-verified.
            {isOwner && (
              <>
                {' '}
                <a href="/settings/org_settings">Re-configure domain in Org Settings →</a>
              </>
            )}
          </p>
        </div>
      )}

      <div className="cadence-detail-page--content">
        <div className="cadence-detail-page--header-card">
          <CadenceDetailHeader
            name={cadence.name}
            status={cadence.status}
            subtitle={null}
            tags={rawCadence?.tags}
            blueprintName={blueprintName}
            isEditingName={actions.isEditingName}
            editName={actions.editName}
            setEditName={actions.setEditName}
            onSubmitRename={availableActions.includes('edit') ? actions.handleSubmitRename : undefined}
            onCancelRename={availableActions.includes('edit') ? actions.handleCancelRename : undefined}
            onStartRename={availableActions.includes('edit') ? actions.handleStartRename : undefined}
            actions={
              <>
                <Button type="secondary" onClick={() => EverTrue.Navigator(`/cadences-v2/${cadenceId}/edit`, true)}>
                  <Icon icon="eye" />
                  <span>View Setup</span>
                </Button>
                {availableActions.includes('share') && (
                  <Button type="secondary" onClick={() => setShowShareModal(true)}>
                    <Icon icon="share" />
                    <span>Share</span>
                  </Button>
                )}
              </>
            }
            overflowActions={[
              availableActions.includes('edit') && {
                label: 'Edit',
                icon: 'edit',
                onClick: () => EverTrue.Navigator(`/cadences-v2/${cadenceId}/edit`, true),
              },
              availableActions.includes('edit') && {
                label: 'Rename',
                icon: 'edit',
                onClick: actions.handleStartRename,
              },
              availableActions.includes('enroll_members') && {
                label: 'Add Recipients',
                icon: 'group-add',
                onClick: () => actions.setShowContactPicker(true),
              },
              // Activate (Draft → Active)
              availableActions.includes('activate') && {
                label: 'Activate',
                icon: 'play-arrow',
                onClick: async () => {
                  const hasEmailSteps = hasAutomatedEmailSteps(rawCadence?.events || []);
                  if (hasEmailSteps && domainStatus === 'not_configured') {
                    EverTrue.Alert.error(
                      'Email sending requires a verified domain. Ask your organization Owner to configure a sending domain in Organization Settings.'
                    );
                    return;
                  }
                  if (hasEmailSteps && domainStatus === 'pending') {
                    EverTrue.Alert.error(
                      "Your organization's sending domain is not yet verified. DNS verification can take up to 48 hours. Try again once verification is complete."
                    );
                    return;
                  }
                  try {
                    const result = await activateCadence(cadenceId, rawCadence?.version);
                    if (!result?.success) throw new Error(result?.message || 'Activation failed');
                    EverTrue.Alert.success('Cadence activated');
                    refetch();
                  } catch (err) {
                    EverTrue.Alert.error(
                      err.code === 'ATTACHMENT_TOO_LARGE'
                        ? 'Publishing Failed: One or more automated email steps include an attachment that is over the 7MB limit. Please remove / reduce the size of these attachments in order to publish.'
                        : `Activation failed: ${err.message}`
                    );
                  }
                },
              },
              // Pause / Resume
              availableActions.includes('pause') && {
                label: 'Pause',
                icon: 'pause',
                onClick: async () => {
                  try {
                    await updateCadence(cadenceId, rawCadence?.version, { status: 'paused' });
                    EverTrue.Alert.success('Cadence paused');
                    refetch();
                  } catch (err) {
                    EverTrue.Alert.error(`Pause failed: ${err.message}`);
                  }
                },
              },
              availableActions.includes('resume') && {
                label: 'Resume',
                icon: 'play-arrow',
                onClick: async () => {
                  try {
                    const result = await updateCadence(cadenceId, rawCadence?.version, { status: 'active' });
                    if (!result?.success) throw new Error(result?.message || 'Could not resume cadence.');
                    EverTrue.Alert.success('Cadence resumed');
                    refetch();
                  } catch (err) {
                    EverTrue.Alert.error(
                      err.code === 'ATTACHMENT_TOO_LARGE'
                        ? 'Activation Failed: One or more automated email steps include an attachment that is over the 7MB limit. This cadence cannot be re-activated.'
                        : `Resume failed: ${err.message}`
                    );
                  }
                },
              },
              availableActions.includes('archive') && {
                label: 'Archive',
                icon: 'archive',
                onClick: () => {
                  setConfirmConfig({
                    title: 'Archive Cadence',
                    body: 'Active members will be stopped.',
                    confirmLabel: 'Archive',
                    isDanger: false,
                    onConfirm: async () => {
                      try {
                        await updateCadence(cadenceId, rawCadence?.version, { status: 'archived' });
                        EverTrue.Alert.success('Cadence archived. Cleaning up incomplete tasks...');
                        createTaskCleanupJob([cadenceId]).catch((e) =>
                          console.warn('[cadences] background fetch failed:', e.message)
                        );
                        refetch();
                      } catch (err) {
                        EverTrue.Alert.error(`Archive failed: ${err.message}`);
                      }
                    },
                  });
                },
              },
              availableActions.includes('duplicate') && {
                label: 'Save as Blueprint',
                icon: 'note-add',
                onClick: actions.handleShowShellInput,
              },
              availableActions.includes('duplicate') && {
                label: 'Duplicate',
                icon: 'content-copy',
                iconKit: 'gt2',
                onClick: actions.handleDuplicate,
              },
              availableActions.includes('delete') && {
                label: 'Archive & Remove Tasks',
                icon: 'delete',
                onClick: () => {
                  setConfirmConfig({
                    title: 'Archive & Remove Tasks',
                    body: 'Incomplete tasks will be removed. You can restore this cadence later.',
                    confirmLabel: 'Archive',
                    isDanger: true,
                    onConfirm: async () => {
                      try {
                        await deleteCadence(cadenceId, rawCadence?.version);
                        createTaskCleanupJob([cadenceId]).catch((e) =>
                          console.warn('[cadences] background fetch failed:', e.message)
                        );
                        EverTrue.Alert.success('Cadence archived');
                        handleBackToCadences();
                      } catch (err) {
                        EverTrue.Alert.error(`Archive failed: ${err.message}`);
                      }
                    },
                  });
                },
                isDanger: true,
              },
            ].filter(Boolean)}
          />

          {/* ShellNameBar replaced by DuplicateCadenceModal in blueprint mode */}

          {/* Metric Progress — multi-metric from cadenceMetricProgress, fallback to single goal */}
          {metricProgressList.length > 0 ? (
            <div className="cadence-detail-page--metrics-section">
              {metricProgressList.map((metric) => {
                const currentPct = metric.currentPercentage ?? 0;
                const targetPct = metric.targetPercentage ?? 0;
                const pctOfTarget = targetPct > 0 ? getRoundedPercentage(currentPct, targetPct) : 0;
                const barWidth = targetPct > 0 ? Math.min((currentPct / targetPct) * 100, 100) : 0;
                return (
                  <div key={metric.id} className="cadence-detail-page--metric-row">
                    <div className="cadence-detail-page--goal-header">
                      <span className="cadence-detail-page--goal-text">
                        Goal: {METRIC_TYPE_LABELS[metric.metricType] || metric.metricType}
                      </span>
                    </div>
                    <div className="cadence-detail-page--goal-bar">
                      <div className="cadence-detail-page--goal-track">
                        <div className="cadence-detail-page--goal-fill" style={{ width: `${barWidth}%` }} />
                      </div>
                      <span className="cadence-detail-page--goal-pct">{currentPct}%</span>
                    </div>
                    <div className="cadence-detail-page--goal-footer">
                      <span className="cadence-detail-page--goal-progress">{pctOfTarget}% of target</span>
                      <span className="cadence-detail-page--goal-target">Target: {targetPct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            cadence.goal &&
            (() => {
              const currentPct = cadence.goal.currentPercent ?? 0;
              const targetPct = cadence.goal.targetPercent ?? 0;
              const pctOfTarget = targetPct > 0 ? getRoundedPercentage(currentPct, targetPct) : 0;
              const barWidth = targetPct > 0 ? Math.min((currentPct / targetPct) * 100, 100) : 0;
              return (
                <div className="cadence-detail-page--goal-container">
                  <div className="cadence-detail-page--goal-header">
                    <span className="cadence-detail-page--goal-text">Goal: {cadence.goal.label}</span>
                  </div>
                  <div className="cadence-detail-page--goal-bar">
                    <div className="cadence-detail-page--goal-track">
                      <div className="cadence-detail-page--goal-fill" style={{ width: `${barWidth}%` }} />
                    </div>
                    <span className="cadence-detail-page--goal-pct">{currentPct}%</span>
                  </div>
                  <div className="cadence-detail-page--goal-footer">
                    <span className="cadence-detail-page--goal-progress">{pctOfTarget}% of target</span>
                    <span className="cadence-detail-page--goal-target">Target: {targetPct}%</span>
                  </div>
                </div>
              );
            })()
          )}
          {(metricProgressList.length > 0 || cadence.goal) && (
            <div className="cadence-detail-page--refresh-row">
              <Button type="simple" onClick={handleRefreshMetrics} disabled={refreshingMetrics}>
                <Icon icon="sync" />
                <span>{refreshingMetrics ? 'Refreshing...' : 'Refresh Metrics'}</span>
              </Button>
            </div>
          )}
        </div>

        {/* Enrollment progress banner */}
        {enrollmentInProgress && (
          <div className="cadence-enrollment-banner">
            <div className="cadence-enrollment-banner--spinner" />
            <span>
              Enrolling recipients&hellip;
              {polledJob.totalItems > 0 &&
                ` (${(polledJob.processedItems || 0).toLocaleString()} of ${polledJob.totalItems.toLocaleString()})`}
            </span>
          </div>
        )}
        {jobFailed && polledJob && (
          <div className="cadence-enrollment-banner is-error">
            <Icon icon="error-outline" />
            <span>Enrollment failed{polledJob.errorMessage ? `: ${polledJob.errorMessage}` : ''}</span>
          </div>
        )}

        {rawCadence?.audiences?.length > 0 && (
          <CadenceAudienceSources
            audiences={rawCadence.audiences}
            onDisconnect={async (audienceId) => {
              await deleteCadenceAudience(audienceId);
              refetch();
            }}
            canManage={isCadenceOwner}
          />
        )}

        {/* Overall Performance Table */}
        <div className="cadence-detail-page--perf-section">
          <div className="cadence-detail-page--perf-header">
            <h2 className="cadence-detail-page--section-title">Overall Performance</h2>
          </div>
          <OverallPerformanceTable steps={steps} cadence={rawCadence} loading={statsLoading} error={statsError} />
        </div>

        {/* Performance & Recipients Section */}
        <div className="cadence-detail-page--combined-card" ref={stepPerformanceRef}>
          <StepFilterHeader
            steps={steps}
            cadence={rawCadence}
            selectedStepFilter={selectedStepFilter}
            onStepFilterChange={setSelectedStepFilter}
            timeframeFilter={timeframeFilter}
            onTimeframeChange={setTimeframeFilter}
          />
          {selectedStep && <StepStatsSummary stepData={selectedStep} />}
          {selectedStep && (
            <div className="cadence-detail-page--chart-section">
              <CadencePerformanceChart
                cadenceId={cadenceId}
                cadenceEventId={selectedStepFilter}
                timeframe={timeframeFilter}
                title={
                  selectedStep?.cadenceEventReferenceNumber != null
                    ? `Step Performance (ID ${selectedStep.cadenceEventReferenceNumber})`
                    : 'Step Performance'
                }
                isManualStep={selectedStep?.isAutomated === false}
              />
            </div>
          )}
          <div className="cadence-detail-page--recipients-section">
            <CadenceRecipientsTable
              cadenceId={cadenceId}
              cadenceStatus={cadence?.status}
              onViewDetails={setSelectedRecipient}
              stepEventId={selectedStepFilter && selectedStepFilter !== 'full_cadence' ? selectedStepFilter : undefined}
              stepMemberIds={stepMemberIds}
              stepRecipientMap={stepRecipientMap}
              steps={steps}
              timeframeFilter={timeframeFilter}
              title={selectedStep ? 'Step Recipients' : undefined}
              refreshKey={stepRecipientsRefreshCount}
              onMembersChanged={() => {
                refetch();
                refetchStats();
              }}
            />
          </div>
        </div>
      </div>

      {/* Recipient Detail Slide-in Panel */}
      {selectedRecipient && (
        <RecipientDetailPanel
          recipient={selectedRecipient}
          cadenceId={cadenceId}
          cadenceName={cadence.name}
          cadence={rawCadence}
          steps={steps}
          onClose={() => setSelectedRecipient(null)}
          onRemove={availableActions.includes('remove_members') ? handleRemoveRecipient : undefined}
          onSkipStep={availableActions.includes('remove_members') ? handleSkipStep : undefined}
          successMetric={
            metricProgressList.length > 0
              ? {
                  metricType: metricProgressList[0].metricType,
                  label: METRIC_TYPE_LABELS[metricProgressList[0].metricType] || metricProgressList[0].metricType,
                  description: metricProgressList[0].description || null,
                  metricConfig: rawCadence?.successMetrics?.[0]?.metricConfig || {},
                  targetPercentage: rawCadence?.successMetrics?.[0]?.targetPercentage || null,
                }
              : undefined
          }
        />
      )}

      {/* Contact Picker Modal */}
      <Modal isOpen={actions.showContactPicker} closeModal={() => actions.setShowContactPicker(false)}>
        <ContactPicker
          header="Add Recipients to Cadence"
          description="Search for contacts to enroll in this cadence"
          onSelect={async (contacts) => {
            await actions.handleAddPeople(contacts);
            refetchStats();
            setStepRecipientsRefreshCount((c) => c + 1);
          }}
          requestHide={() => actions.setShowContactPicker(false)}
        />
      </Modal>

      <DuplicateCadenceModal
        isOpen={actions.showDuplicateModal}
        onClose={() => actions.setShowDuplicateModal(false)}
        onDuplicate={actions.handleDuplicateConfirm}
        entityName={cadence?.name}
        entityType="cadence"
      />

      <DuplicateCadenceModal
        isOpen={actions.showBlueprintModal}
        onClose={() => actions.setShowBlueprintModal(false)}
        onDuplicate={actions.handleSaveAsBlueprintConfirm}
        entityName={cadence?.name}
        entityType="blueprint"
      />

      <CadenceSharingModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          refetch();
        }}
        cadenceId={cadenceId}
        cadenceVersion={rawCadence?.version}
        cadenceName={cadence?.name}
        ownerId={rawCadence?.ownerUserId}
      />

      <ConfirmModal
        isOpen={!!confirmConfig}
        onClose={() => setConfirmConfig(null)}
        title={confirmConfig?.title || ''}
        body={confirmConfig?.body || ''}
        confirmLabel={confirmConfig?.confirmLabel}
        isDanger={confirmConfig?.isDanger}
        onConfirm={confirmConfig?.onConfirm || (() => {})}
      />
    </div>
  );
};

CadenceDetailPage.propTypes = {
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CadenceDetailPage;
